import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Alert,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { sharePayload } from "../../../uiComponents/ShareUtils";
import copyToClipboard from "../../../../helper/copyToClipBoard";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import AnimatedSuccessIcon from "../../common/AnimatedSuccessView";
import CopyIcon from "../../../../../../../assets/images/copy.svg";
import ReceiptIcon from "../../../../../../../assets/images/receipt.svg";
import ShareIcon from "../../../../../../../assets/images/share.ios.svg";

/**
 * Purpose:
 * Final confirmation screen. Tells the customer to head to the cashier and
 * shows the staged transaction details.
 *
 * Why:
 * Last step of the Send Money workflow.
 */
export function HeadToCashierScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const journeyData = useJourneyStore((state) => state.journeyData);
  const resetJourneyData = useJourneyStore((state) => state.resetJourneyData);
  const [sharing, setSharing] = useState(false);

  const { receiverInfo, receiveAmount, receiveCurrency, selectedServiceOption } =
    journeyData;

  const referenceNumber = selectedServiceOption?.transactionId || "";

  const receiverFirstName = receiverInfo.firstName || "";

  const receiveAmountFormatted = useMemo(() => {
    return receiveAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [receiveAmount]);

  const buildReceiptText = () => {
    return `MoneyGram Receipt\n\nReceiver: ${receiverFirstName}\nAmount: ${receiveAmountFormatted} ${receiveCurrency}\nConfirmation: ${referenceNumber}\nStatus: Staged\n\nThank you for using MoneyGram.`;
  };

  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const result = await sharePayload({
        title: "MoneyGram Receipt",
        message: buildReceiptText(),
      });
      if (Platform.OS === "web" && (result as any).usedClipboard) {
        Alert.alert(
          "Share not available",
          "Share sheet is not supported in this browser. Receipt text has been copied to clipboard."
        );
      }
    } catch (err: any) {
      console.error("Share error:", err);
      Alert.alert("Unable to share", err?.message || "An error occurred while sharing.");
    } finally {
      setSharing(false);
    }
  }, [receiverFirstName, receiveAmountFormatted, receiveCurrency, referenceNumber]);

  const onDone = () => {
    resetJourneyData();
    navigate("/");
  };

  return (
    <View style={styles.container} testID="head-to-cashier-screen">
      <View style={{ alignItems: "center" }}>
        <AnimatedSuccessIcon />

        <Text style={styles.title}>{t("HEAD_TO_CASHIER")}</Text>
        <Text style={styles.subtitle}>{t("THANK_YOU_MONEYGRAM")}</Text>

        <Text style={styles.receiverLine}>
          {receiverFirstName} {t("WILL_RECEIVE")}
        </Text>
        <Text style={styles.amount}>
          {receiveAmountFormatted}
          <Text style={styles.currencyStyle}> {receiveCurrency}</Text>
        </Text>

        <Text style={[styles.referenceLabel, { marginBottom: 9 }]}>
          {t("STATUS")} <Text style={styles.referenceNumber}>Staged</Text>
        </Text>
      </View>

      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>{t("REFERENCE")}</Text>
        <TouchableOpacity
          style={styles.referenceCopy}
          onPress={() => copyToClipboard(referenceNumber)}
          testID="referenceNumberCopy"
        >
          <Text style={styles.referenceNumber} testID="confirmationCode">
            {referenceNumber}
          </Text>
          <CopyIcon width={24} height={24} />
        </TouchableOpacity>
      </View>

      <Text style={styles.instructionTitle}>{t("WHAT_DO_I_SAY")}</Text>
      <Text style={styles.instruction}>{t("LET_THE_CASHIER_KNOW")}</Text>

      <View style={styles.actionsRow}>
        <TouchableOpacity
          style={[styles.actionBox, { marginRight: 12 }]}
          onPress={onShare}
          testID="shareReceiptButton"
        >
          <View style={styles.receiptView}>
            <ShareIcon width={24} height={24} />
          </View>
          <Text style={styles.actionLabel}>{t("SHARE_RECEIPT")}</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.actionBox} onPress={onDone} testID="doneButton">
          <View style={styles.receiptView}>
            <ReceiptIcon width={24} height={24} />
          </View>
          <Text style={styles.actionLabel}>Done</Text>
        </TouchableOpacity>
      </View>

      {sharing && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>{t("PREPARING_SHARE")}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  title: {
    fontSize: 28,
    color: Colors.darkGrey,
    fontWeight: "500",
    fontFamily: "Rubik",
    lineHeight: 34,
    marginBottom: 8,
    marginTop: 24,
  },
  subtitle: {
    fontSize: 16,
    color: Colors.black,
    fontWeight: "400",
    fontFamily: "Rubik",
    marginBottom: 32,
  },
  receiverLine: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 22,
    marginBottom: 6,
  },
  amount: {
    fontSize: 40,
    color: Colors.titleTextColor,
    fontWeight: "500",
    fontFamily: "Rubik",
    marginBottom: 6,
    lineHeight: 46,
  },
  currencyStyle: {
    fontSize: 24,
    fontFamily: "Rubik",
    fontWeight: "500",
    color: Colors.infoSubValue,
    lineHeight: 30,
  },
  referenceRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 36,
    backgroundColor: Colors.buttonTextColor,
    borderRadius: 8,
    paddingVertical: 12,
  },
  referenceLabel: {
    fontSize: 16,
    color: Colors.darkGrey,
    fontWeight: "500",
    fontFamily: "Rubik",
    lineHeight: 24,
    marginRight: 12,
  },
  referenceCopy: {
    flexDirection: "row",
    alignItems: "center",
  },
  referenceNumber: {
    fontSize: 16,
    color: Colors.termsAndConditionsBlue,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: 26,
    marginRight: 12,
  },
  instructionTitle: {
    fontSize: 20,
    color: Colors.darkGrey,
    lineHeight: 26,
    marginBottom: 8,
    fontFamily: "Rubik",
    fontWeight: "500",
    textAlign: "left",
  },
  instruction: {
    fontSize: 16,
    color: Colors.black,
    marginBottom: 24,
    fontFamily: "Rubik",
    fontWeight: "400",
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 39,
    width: "100%",
  },
  actionBox: {
    flex: 1,
    padding: 16,
    borderColor: "#ddd",
    backgroundColor: Colors.searchCompanyBackground,
    borderRadius: 16,
  },
  receiptView: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.secondaryText,
    marginBottom: 12,
    borderRadius: 30,
  },
  actionLabel: {
    fontSize: 16,
    color: Colors.titleTextColor,
    fontFamily: "Rubik",
    fontWeight: "500",
    lineHeight: 24,
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayText: {
    color: "#fff",
    marginTop: 10,
  },
});
